// src/services/paymentService.js
import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '../firebase/config';
import {
  doc,
  updateDoc,
  getDoc,
  setDoc,
  increment,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';

import { createLogger } from '../utils/logger';

const logger = createLogger('paymentService');

const REPORT_PRICE = 4.99;

export const PaymentService = {
  /**
   * Force refresh of user payment status with retries
   * @param {string} userId
   * @returns {Promise<boolean>}
   */
  forceRefreshPaymentStatus: async userId => {
    logger.debug(`🔄 Force refreshing payment status for user: ${userId}`);
    return await PaymentService.checkUserPurchaseStatus(userId, {
      retries: 5,
      delay: 2000,
      skipCache: true,
    });
  },
  /**
   * Create a Stripe checkout session
   * @param {Object} params
   * @returns {Promise<Object>}
   */
  createCheckoutSession: async params => {
    try {
      if (!params || typeof params !== 'object') {
        throw new Error('❌ Payment parameters must be an object.');
      }

      const {
        amount,
        productName,
        successUrl,
        cancelUrl,
        userId: providedUserId,
      } = params;

      const currentUser = auth.currentUser;
      const userId = providedUserId || currentUser?.uid || 'test-user';

      if (typeof amount !== 'number' || isNaN(amount)) {
        throw new Error("❌ 'amount' must be a valid number.");
      }
      if (!productName || typeof productName !== 'string') {
        throw new Error("❌ 'productName' is required and must be a string.");
      }
      if (!successUrl || typeof successUrl !== 'string') {
        throw new Error("❌ 'successUrl' is required and must be a string.");
      }
      if (!cancelUrl || typeof cancelUrl !== 'string') {
        throw new Error("❌ 'cancelUrl' is required and must be a string.");
      }

      const payload = {
        userId,
        amount,
        productName,
        successUrl,
        cancelUrl,
      };

      logger.debug('📤 Sending to Stripe checkout function:', payload);

      const createStripeCheckout = httpsCallable(
        functions,
        'createStripeCheckout'
      );
      const response = await createStripeCheckout(payload);

      logger.debug('✅ Stripe session created:', response.data);
      return response.data;
    } catch (error) {
      logger.error('🔥 createCheckoutSession error:', error.message);
      throw error;
    }
  },

  checkUserPurchaseStatus: async (userId, options = {}) => {
    const { retries = 3, delay = 1000 } = options;

    try {
      if (!userId) return false;

      for (let attempt = 1; attempt <= retries; attempt++) {
        try {
          logger.debug(
            `🔍 Checking purchase status (attempt ${attempt}/${retries}) for user: ${userId}`
          );

          const userRef = doc(db, 'users', userId);
          const userDoc = await getDoc(userRef);

          if (userDoc.exists()) {
            const userData = userDoc.data();
            const hasPurchased = userData.hasPurchasedReport === true;

            logger.debug(`📊 Purchase status for ${userId}:`, {
              hasPurchased,
              purchaseDate: userData.purchaseDate,
              paymentAmount: userData.paymentAmount,
              paymentId: userData.paymentId,
              attempt,
            });

            if (hasPurchased) {
              return true;
            }

            // If not purchased and we have more retries, wait before retrying
            if (attempt < retries) {
              logger.debug(`⏳ Payment not found, retrying in ${delay}ms...`);
              await new Promise(resolve => setTimeout(resolve, delay));
            }
          } else {
            logger.warn(`👤 User document not found for ${userId}`);
            if (attempt < retries) {
              await new Promise(resolve => setTimeout(resolve, delay));
            }
          }
        } catch (attemptError) {
          logger.warn(`⚠️ Attempt ${attempt} failed:`, attemptError.message);
          if (attempt === retries) {
            throw attemptError;
          }
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }

      return false;
    } catch (error) {
      logger.error('❌ Error checking user purchase status:', error);
      return false;
    }
  },

  updateUserAfterPurchase: async (userId, paymentDetails) => {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      const userExists = userDoc.exists();

      const data = {
        hasPurchasedReport: true,
        purchaseDate: new Date().toISOString(),
        paymentAmount: REPORT_PRICE,
        paymentId: paymentDetails?.id || '',
        paymentMethod: paymentDetails?.method || '',
        lastPaymentUpdate: serverTimestamp(),
      };

      logger.debug(`💰 Updating user ${userId} with payment data:`, data);

      if (!userExists) {
        await setDoc(userRef, {
          ...data,
          createdAt: serverTimestamp(),
          quizAttempts: 0,
        });
        logger.debug(`🆕 Created new user document for ${userId}`);
      } else {
        await updateDoc(userRef, data);
        logger.debug(`📝 Updated existing user document for ${userId}`);
      }

      // Verify the update was successful
      const verifyDoc = await getDoc(userRef);
      const verifyData = verifyDoc.data();

      if (verifyData?.hasPurchasedReport === true) {
        logger.debug(`✅ Payment update verified for user ${userId}`);
      } else {
        logger.error(
          `❌ Payment update verification failed for user ${userId}`,
          verifyData
        );
        throw new Error('Payment update verification failed');
      }
    } catch (error) {
      logger.error('❌ Error updating user after purchase:', error);
      throw error;
    }
  },

  saveQuizResults: async (userId, results) => {
    try {
      if (!userId) throw new Error('User ID required for saving quiz results');
      const userRef = doc(db, 'users', userId);
      const userExists = (await getDoc(userRef)).exists();

      const updateData = {
        quizResults: results,
        lastQuizDate: new Date().toISOString(),
        quizAttempts: increment(1),
      };

      if (!userExists) {
        await setDoc(userRef, {
          ...updateData,
          createdAt: serverTimestamp(),
          hasPurchasedReport: false,
        });
      } else {
        await updateDoc(userRef, updateData);
      }

      logger.debug(`✅ Quiz results saved for ${userId}`);
    } catch (error) {
      logger.error('❌ Error saving quiz results:', error);
      throw error;
    }
  },

  getSavedQuizResults: async userId => {
    try {
      if (!userId) return null;
      const userDoc = await getDoc(doc(db, 'users', userId));
      return userDoc.exists() ? userDoc.data().quizResults || null : null;
    } catch (error) {
      logger.error('❌ Error retrieving quiz results:', error);
      return null;
    }
  },
};

export default PaymentService;
